import {
  Box,
  Divider,
  Link,
  Typography,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { unwrapResult } from '@reduxjs/toolkit';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  getCompanyPartnerRelations,
  unlinkCompanyFromPartner,
} from '../../../../store/thunks/dashboardThunk';
import { useCustomEventTracking } from '../../../../common/useCustomEventTracking';
import { Logger } from '../../../../config/logging/logger';
import { selectUpdateCompanyPartnerRelationState } from '../../../../store/slices/dashboardSlice';
import OptimusButton from '../../../../common/library/Button/Button';
import { getCanonicalPartnerName } from '../../../../common/constants';

interface UnlinkPartnerCardProps {
  partnerId: string;
  partnerName: string;
  partnerCode?: string;
  title: string;
  description: string | string[] | null;
  setOpenUnlink?: (event: boolean) => void;
}

export function UnlinkPartnerCard({
  partnerId, partnerName, partnerCode, title, description, setOpenUnlink,
}: UnlinkPartnerCardProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const { trackPartnerEnrollment } = useCustomEventTracking();
  const { company } = useAppSelector(selectMemberInformation);
  const updateCompanyPartnerRelationState =
    useAppSelector(selectUpdateCompanyPartnerRelationState);
  const logger = Logger.create('UnlinkPartnerCard');
  const [isUnlinking, setIsUnlinking] = useState(false);

  const handleUnlink = async () => {
    if (isUnlinking) return;
    setIsUnlinking(true);
    try {
      const companyId = company?.id;
      if (!companyId) {
        throw new Error('Company not found');
      }

      await dispatch(unlinkCompanyFromPartner({
        partnerId,
        companyId,
      })).then(unwrapResult);
      dispatch(getCompanyPartnerRelations(companyId));

      setOpenUnlink?.(false);
      // Derive the event name from the stable code so a rename does not change it.
      const partnerKeyName = getCanonicalPartnerName(partnerCode, partnerName);
      trackPartnerEnrollment('Unlink', `${partnerKeyName.replace(/\s+/g, '')}Account`);
    } catch (error: unknown) {
      logger.error(Logger.format(error));
    } finally {
      setIsUnlinking(false);
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: theme.spacing(2.5),
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      <Divider sx={{
        marginBottom: theme.spacing(3),
        marginTop: theme.spacing(2),
      }} />
      <Box sx={{
        marginBottom: theme.spacing(3),
      }}>
        {description && (Array.isArray(description) ? (
          description.map((line, index) => (
            <Typography
              key={index}
              component='div'
              sx={{
                marginBottom: index < description.length - 1 ? theme.spacing(1.5) : 0,
              }}
            >
              {line}
            </Typography>
          ))
        ) : (
          <Typography component='span'>
            {description}
          </Typography>
        ))}
      </Box>
      <Divider sx={{ marginBottom: theme.spacing(3) }} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-evenly',
          width: '50%',
          ml: 'auto',
          [theme.breakpoints.down('sm')]: {
            width: '90%',
          },
          [theme.breakpoints.between('sm', 'md')]: {
            width: '60%',
          },
        }}
      >
        <Link
          component="button"
          variant="body2"
          onClick={() => setOpenUnlink?.(false)}
          underline='none'
        >
          {t('button.cancel')}
        </Link>
        <OptimusButton
          size={'s'}
          fullWidth={false}
          buttonText={updateCompanyPartnerRelationState === 'pending' ?
            <CircularProgress color="inherit" size={24} /> : t('button.confirmAndLink') || ''
          }
          onClick={handleUnlink}
        />
      </Box>
    </Box>
  );
}
